class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  escapeHtml(value = "") {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  getTags(product) {
    if (Array.isArray(product.tags)) return product.tags;
    if (typeof product.tags === "string") {
      return product.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }

  hasTag(tags, needle) {
    return tags.some((tag) => String(tag).includes(needle));
  }

  /*
    Mirrors the bulk-price floor used by the Alternix product-grid card.
    This is a display price only: recommendation cards still link to PDP.
  */
  getDisplayPrice(product) {
    const tags = this.getTags(product);

    const fromRules = [
      ["Item_pro1_Buy any 3 for £10", 3.00],
      ["Item_pro_Buy any 3 for £6", 3.00],
      ["Item_pro_Buy any 5 for £12", 3.00],
      ["Zyn", 3.50],
      ["4 for 10", 2.25],
      ["Multibuy Payday 20-1.99", 2.49],
      ["Multibuy Pouch 4.49-2.49", 2.49],
      ["Multibuy Pouch 3.95-3.15", 3.15],
      ["Multibuy Pouch 3.95-2.50", 2.50],
      ["Multibuy Pouch 4.49-2.49", 2.49],
      ["Multibuy Pouch 4.95-4.20", 4.20],
      ["Multibuy Pouch 4.95-4.00", 4.00],
      ["Multibuy Pouch 5.50-4.25", 4.25],
      ["Multibuy Pouch 5.95-5.00", 5.00],
      ["Multibuy Pouch 5.95-4.35", 4.35],
      ["Multibuy Pouch 6.50-4.50", 4.50],
      ["Multibuy Strips 3.95-3.15", 3.15]
    ];

    for (const [tagName, price] of fromRules) {
      if (this.hasTag(tags, tagName)) {
        return {
          prefix: "From",
          price,
          compareAt: null
        };
      }
    }

    const price = Number(product.price || 0) / 100;
    const compareAtRaw = Number(product.compare_at_price || 0);
    const compareAt = compareAtRaw > Number(product.price || 0)
      ? compareAtRaw / 100
      : null;

    return {
      prefix: "",
      price,
      compareAt
    };
  }

  formatMoney(value) {
    return `£${Number(value).toFixed(2)}`;
  }

  createProduct(product) {
    const productPath = window.currentLocale
      ? `/${window.currentLocale}/products/${product.handle}`
      : `/products/${product.handle}`;

    const title = this.escapeHtml(product.title);
    const url = this.escapeHtml(productPath);
    const imageUrl = product.images?.[0] || product.featured_image || "";
    const image = this.escapeHtml(imageUrl);
    const displayPrice = this.getDisplayPrice(product);

    const priceMarkup = displayPrice.compareAt
      ? `
        <span class="recommendation-card__price-was">${this.formatMoney(displayPrice.compareAt)}</span>
        <span class="recommendation-card__price-value">${this.formatMoney(displayPrice.price)}</span>
      `
      : `
        ${displayPrice.prefix ? `<span class="recommendation-card__price-prefix">${displayPrice.prefix}</span>` : ""}
        <span class="recommendation-card__price-value">${this.formatMoney(displayPrice.price)}</span>
      `;

    return `
      <article
        class="recommendation-card swiper-slide"
        data-handle="${this.escapeHtml(product.handle)}"
        role="listitem"
      >
        <a
          href="${url}"
          class="recommendation-card__media-link"
          aria-label="View ${title}"
        >
          ${image ? `<img class="recommendation-card__image" src="${image}" alt="${title}" loading="lazy">` : ""}
        </a>

        <a href="${url}" class="recommendation-card__title-link">
          <h3 class="recommendation-card__title">${title}</h3>
        </a>

        <div class="recommendation-card__price">
          ${priceMarkup}
        </div>

        <a
          href="${url}"
          class="recommendation-card__cta"
          aria-label="View ${title}"
        >
          <span>View</span>
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M5 3.5 9.5 8 5 12.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </a>
      </article>
    `;
  }

  connectedCallback() {
    const productId = this.getAttribute("data-id");
    const limit = this.getAttribute("data-count") || 6;
    const container = this.querySelector("#recommended-products-inect");
    const header = this.querySelector(".product-recommendations__header");

    if (!productId || !container) return;

    fetch(
      window.Shopify.routes.root +
        `recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`
    )
      .then((response) => {
        if (!response.ok) throw new Error("Unable to load recommendations.");
        return response.json();
      })
      .then(({ products = [] }) => {
        if (!products.length) return;

        container.innerHTML = products
          .map((product) => this.createProduct(product))
          .join("");

        if (header) header.hidden = false;

        /*
          The recommendation cards are injected after the page's initial
          carousel setup. Let the theme know the DOM has changed.
        */
        this.dispatchEvent(
          new CustomEvent("recommendations:loaded", {
            bubbles: true,
            detail: { count: products.length }
          })
        );

        window.dispatchEvent(new Event("resize"));
      })
      .catch((error) => {
        console.error("Product recommendations:", error);
      });
  }
}

if (!customElements.get("product-recommendations")) {
  customElements.define("product-recommendations", ProductRecommendations);
}
